import axios from 'axios'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'https://api.drinx.jo'

export const api = axios.create({
  baseURL: `${BASE_URL}/admin`,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Server-emitted codes that mean "your token was rejected". `token_revoked`
// = token_version bumped (password reset / role change). `invalid_token` =
// JWT verification threw (signature bad, expired). Both need a re-login.
//
// NOT included: `unauthorized`. That code fires whenever the request lacked
// a valid Authorization header — which usefully includes "the server can't
// find your admin row" but also includes "no Bearer header sent at all",
// and the latter is a request we already know is unauthenticated. Treating
// it as session-death caused a re-entry loop after ANY other 401:
//   1. First 401 (whatever reason) → interceptor clears token, redirects.
//   2. In-flight React Query poll fires before the redirect completes.
//   3. Request interceptor reads localStorage → empty → sends without header.
//   4. Server returns 401 code=unauthorized (no header).
//   5. Old handler kicked again on that reply.
// So we detect the "unauthorized-but-we-sent-a-token" case explicitly
// below and treat it the same as the invalid-token bucket; when we didn't
// send a token, the 401 is just a natural response to an anon request and
// we let it propagate.
const REAL_SESSION_DEATH_CODES = new Set(['token_revoked', 'invalid_token'])

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const path: string | undefined = err.config?.url
      const code: string | undefined = err.response?.data?.code
      const sentBearer = typeof err.config?.headers?.Authorization === 'string'
        && (err.config.headers.Authorization as string).startsWith('Bearer ')

      // /auth/login always returns 401 on wrong password — that's the login
      // page reacting to a typo, not a session death. Never kick from there.
      const isLogin = path?.endsWith('/auth/login') === true

      // Kick only when both:
      //   - the request actually carried a Bearer token (so the server had
      //     something to reject; if we sent nothing there's no session to
      //     kill), AND
      //   - either the code is a real death signal, or it's `unauthorized`
      //     while we did send a token (server can't find our admin row).
      const isDeathCode = code !== undefined && (
        REAL_SESSION_DEATH_CODES.has(code) ||
        (code === 'unauthorized' && sentBearer)
      )

      if (!isLogin && sentBearer && isDeathCode) {
        localStorage.removeItem('admin_token')
        localStorage.removeItem('admin_user')
        window.location.href = '/login'
      }
    }
    return Promise.reject(err)
  }
)

// Public promotions endpoint (not under /admin)
export const publicApi = axios.create({ baseURL: BASE_URL })
